"use client";

import './index.css';
import { jsx } from 'react/jsx-runtime';
import { useState, useRef, useId } from 'react';
import ScrollableContainer from './scrollable-container.js';
import styles from './scrollable.module.scss.js';
import { validateSurface } from '../../globals.js';

/**
 * @component Scrollable
 * @package Scrollable
 * @document ../_docs/code/Scrollable.editor.tsx
 * @document ../_docs/code/Scrollable.snippet.tsx
 * @document ../_docs/code/Scrollable.import.tsx
 */
const Scrollable = ({ containerWidth, containerHeight, minHeight, scrollableId, children, id, surface: surfaceProp = 'lightPrimary', contentStyle, 'data-testid': testId = undefined, onScroll, onScrollStart, onScrollStop, hideTracksWhenNotNeeded = false, hitAreaWidth = 44, minimizeHitArea, overflowOrientation = 'vertical', contentMargin, horizontalScrollbarInset = {
    top: '0px',
    right: '4px',
    bottom: '4px',
    left: '4px',
}, verticalScrollbarInset = {
    top: '4px',
    right: '4px',
    bottom: '4px',
    left: '0px',
}, scrollContainerInset, tabIndex, }) => {
    const [scrollHeight, setScrollHeight] = useState(0);
    const [clientHeight, setClientHeight] = useState(0);
    const [scrollWidth, setScrollWidth] = useState(0);
    const [clientWidth, setClientWidth] = useState(0);
    const scrollId = useRef(null);
    const _useId = useId();
    if (scrollId.current === null)
        scrollId.current = scrollableId || _useId;
    const _onUpdate = (values) => {
        const { scrollHeight: sh, scrollWidth: sw, clientWidth: cw, clientHeight: ch, } = values;
        /* istanbul ignore else -- @preserve */
        if (scrollHeight !== sh)
            setScrollHeight(sh);
        /* istanbul ignore else -- @preserve */
        if (clientHeight !== ch)
            setClientHeight(ch);
        /* istanbul ignore else -- @preserve */
        if (scrollWidth !== sw)
            setScrollWidth(sw);
        /* istanbul ignore else -- @preserve */
        if (clientWidth !== cw)
            setClientWidth(cw);
    };
    const surface = validateSurface(surfaceProp);
    const _thumbVerticalHeight = () => scrollHeight ? (clientHeight / scrollHeight) * clientHeight : 0;
    const _thumbHorizontalWidth = () => scrollWidth ? (clientWidth / scrollWidth) * clientWidth : 0;
    const hideVerticalScroll = _thumbVerticalHeight() >= clientHeight;
    const hideHorizontalScroll = _thumbHorizontalWidth() >= clientWidth;
    let calculatedHeight = containerHeight;
    if (scrollContainerInset || contentMargin) {
        const scrollContainerInsetTop = `${scrollContainerInset && scrollContainerInset.top ? scrollContainerInset.top : '0px'}`;
        const scrollContainerInsetBottom = `${scrollContainerInset && scrollContainerInset.bottom ? scrollContainerInset.bottom : '0px'}`;
        const contentMarginTop = `${contentMargin && contentMargin.top ? contentMargin.top : '0px'}`;
        const contentMarginBottom = `${contentMargin && contentMargin.bottom ? contentMargin.bottom : '0px'}`;
        calculatedHeight = calculatedHeight
            ? `calc(${calculatedHeight} + ${scrollContainerInsetTop} + ${scrollContainerInsetBottom} + ${contentMarginTop} + ${contentMarginBottom})`
            : undefined;
    }
    return (jsx("div", { id: id, className: styles.scrollable, "data-testid": testId ? `${testId}-scrollable` : 'scrollable', style: {
            width: containerWidth,
            height: calculatedHeight,
            minHeight,
        }, children: jsx(ScrollableContainer, { hitAreaWidth: hitAreaWidth, hideVerticalScroll: hideVerticalScroll, hideHorizontalScroll: hideHorizontalScroll, width: containerWidth, height: calculatedHeight, scrollableId: scrollId.current, contentStyle: contentStyle, surface: surface, onUpdate: _onUpdate, "data-testid": testId ? `${testId}-scrollable` : 'scrollable', onScroll: onScroll, onScrollStart: onScrollStart, onScrollStop: onScrollStop, hideTracksWhenNotNeeded: hideTracksWhenNotNeeded, minimizeHitArea: minimizeHitArea, overflowOrientation: overflowOrientation, contentMargin: contentMargin, verticalScrollbarInset: verticalScrollbarInset, horizontalScrollbarInset: horizontalScrollbarInset, scrollContainerInset: scrollContainerInset, tabIndex: tabIndex, children: children }) }));
};

export { Scrollable, Scrollable as default };
